import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Factory, Package } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all' ? `${API}/projects` : `${API}/projects?status=${filter}`;
      const response = await axios.get(url);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filterButtons = [
    { label: 'All Projects', value: 'all' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Ongoing', value: 'Ongoing' },
    { label: 'Upcoming', value: 'Upcoming' },
  ];

  // No sample projects - all projects will be from admin
  const sampleProjects = [];

  const displayProjects = projects.length > 0 ? projects : sampleProjects;
  const filteredProjects = filter === 'all' 
    ? displayProjects 
    : displayProjects.filter(p => p.status === filter);

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">OUR PROJECTS</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">EXECUTED EXCELLENCE</h1>
            <p className="text-xl text-slate-300 max-w-3xl">
              Showcasing our track record of successful industrial projects across India
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="bg-white py-8 border-b border-slate-200" data-testid="project-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {filterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() => setFilter(btn.value)}
                data-testid={`filter-${btn.value}`}
                className={`font-oswald px-6 py-3 transition-all ${
                  filter === btn.value
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-slate-50" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No projects found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border-l-4 border-orange-500 shadow-sm hover:shadow-xl transition-all overflow-hidden group"
                  data-testid={`project-card-${index}`}
                >
                  {/* Status Badge */}
                  <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <span
                      className={`inline-block px-3 py-1 text-xs font-oswald font-bold ${
                        project.status === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : project.status === 'Ongoing'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {project.status}
                    </span>
                  </div>

                  <div className="p-6">
                    <h3 className="font-oswald font-bold text-xl text-slate-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {project.title}
                    </h3>

                    {/* Project Images */}
                    {project.images && project.images.length > 0 && (
                      <div className="mb-4">
                        <img
                          src={project.images[0]}
                          alt={project.title}
                          className="w-full h-48 object-cover mb-2"
                        />
                        {project.images.length > 1 && (
                          <div className="flex gap-2">
                            {project.images.slice(1, 4).map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`${project.title} ${idx + 2}`}
                                className="w-16 h-16 object-cover"
                              />
                            ))}
                            {project.images.length > 4 && (
                              <div className="w-16 h-16 bg-slate-100 flex items-center justify-center text-xs text-slate-600">
                                +{project.images.length - 4}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-start">
                        <Factory className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Client:</strong> {project.client_name}</span>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{project.location}</span>
                      </div>
                      <div className="flex items-start">
                        <Package className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Quantity:</strong> {project.quantity}</span>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-xs text-slate-600 mb-2 font-bold">SCOPE:</p>
                      <p className="text-sm text-slate-700 line-clamp-2">{project.scope}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;